import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, from, throwError, of } from 'rxjs';
import { map, switchMap, catchError, tap, take } from 'rxjs/operators';
import {
  Firestore} from '@angular/fire/firestore';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  Timestamp
} from '@angular/fire/firestore';
import { Storage, ref, uploadBytes, getDownloadURL, deleteObject } from '@angular/fire/storage';
import {
  Meter,
  CreateMeterRequest,
  UpdateMeterRequest,
  AddMeterReadingRequest,
  MeterReading,
  MeterDocument} from '@shared/definitions/meter';
import {
  MeterUtils,
  toStoredMeterProvider
} from '@shared/definitions/meter';
import { groupUIDOfScope, isGroupMeterScope } from '../../property-groups/building-detail/building-detail-meters/building-meters.model';
import { FirestorePathService } from '@core/services/data/firestore-path.service';

@Injectable({
  providedIn: 'root'
})
export class MetersService {
  private metersSubject = new BehaviorSubject<Meter[]>([]);
  public meters$ = this.metersSubject.asObservable();
  
  // Track which property we're currently watching
  private currentPropertyUID: string | null = null;
  
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private firestore: Firestore,
    private storage: Storage,
    private pathService: FirestorePathService
  ) {}

  /**
   * Get the meters sub-collection reference for a property
   */
  private getMetersCollection(propertyUID: string) {
    return collection(this.firestore, this.metersPath(propertyUID));
  }

  /**
   * Chemin de la sous-collection `meters`. Un identifiant de portée d'immeuble
   * (`group:<id>`, voir `groupMeterScope`) désigne les compteurs communs, rangés sous
   * l'immeuble ; tout autre identifiant est un bien (lot compris).
   */
  metersPath(scopeId: string): string {
    if (isGroupMeterScope(scopeId)) {
      return `${this.pathService.getWorkspaceDocument('property_groups', groupUIDOfScope(scopeId))}/meters`;
    }
    return this.pathService.getPropertySubcollection(scopeId, 'meters');
  }

  /**
   * Get a specific meter document reference
   */
  private getMeterDoc(propertyUID: string, meterUID: string) {
    return doc(this.firestore, `${this.metersPath(propertyUID)}/${meterUID}`);
  }

  /**
   * Get all meters for a specific property
   */
  getMetersByProperty(propertyUID: string): Observable<Meter[]> {
    // If we're not already watching this property, start watching it
    if (this.currentPropertyUID !== propertyUID) {
      this.currentPropertyUID = propertyUID;
      this.loadMetersForProperty(propertyUID);
    }
    
    // Return the BehaviorSubject observable so components always get fresh data
    return this.metersSubject.asObservable();
  }

  /**
   * Load meters for a property from Firestore
   */
  private loadMetersForProperty(propertyUID: string): void {
    this.loadingSubject.next(true);
    
    const metersCollection = this.getMetersCollection(propertyUID);
    const metersQuery = query(
      metersCollection,
      orderBy('createdAt', 'desc')
    );

    from(getDocs(metersQuery)).pipe(
      map(snapshot => {
        // Filter out deleted meters in code instead of query
        const meters = snapshot.docs
          .map(doc => ({
            meterUID: doc.id,
            ...doc.data()
          } as Meter))
          .filter(meter => !meter.deleted);
        
        // Sort by type in code
        meters.sort((a, b) => a.type.localeCompare(b.type));
        
        this.metersSubject.next(meters);
        this.loadingSubject.next(false);
        return meters;
      }),
      catchError(error => {
        console.error('Error loading meters:', error);
        this.loadingSubject.next(false);
        
        // If it's a permission error and the collection doesn't exist, return empty array
        if (error.code === 'permission-denied' || error.code === 'not-found') {
          this.metersSubject.next([]);
          return of([]);
        }
        
        return throwError(() => error);
      }),
      take(1)
    ).subscribe();
  }

  /**
   * Get a specific meter by ID
   */
  getMeterById(propertyUID: string, meterUID: string): Observable<Meter | null> {
    const meterDoc = this.getMeterDoc(propertyUID, meterUID);
    
    return from(getDoc(meterDoc)).pipe(
      map(docSnapshot => {
        if (docSnapshot.exists()) {
          return {
            meterUID: docSnapshot.id,
            ...docSnapshot.data()
          } as Meter;
        }
        return null;
      }),
      catchError(error => {
        console.error('Error loading meter:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Create a new meter
   */
  createMeter(meterData: CreateMeterRequest): Observable<string> {
    const metersCollection = this.getMetersCollection(meterData.propertyUID);
    const now = Timestamp.now();

    // Create initial readings array - if initial reading is provided, add it as the first reading
    const initialReadings: MeterReading[] = [];
    if (meterData.currentReading !== undefined && meterData.currentReading !== null && meterData.currentReadingDate) {
      initialReadings.push({
        value: meterData.currentReading,
        date: Timestamp.fromDate(meterData.currentReadingDate),
        type: 'manual',
        notes: 'Initial reading'
      });
    }

    // Firestore rejects documents containing `undefined` values, so we build
    // the payload conditionally and run it through `filterUndefinedValues`
    // before sending. This mirrors the pattern used in `addMeterReading`.
    const baseMeter = {
      ...meterData,
      ...(meterData.currentReadingDate
        ? { currentReadingDate: Timestamp.fromDate(meterData.currentReadingDate) }
        : {}),
      // Override the raw form provider (Date-valued) with the stored shape
      // (Timestamp-valued, nested undefined keys dropped). `undefined` here is
      // stripped by `filterUndefinedValues` below.
      provider: toStoredMeterProvider(meterData.provider),
      readings: initialReadings,
      documents: [],
      status: 'active' as const,
      createdAt: now,
      updatedAt: now,
      deleted: false
    };
    const newMeter = this.filterUndefinedValues(baseMeter);

    return from(addDoc(metersCollection, newMeter)).pipe(
      map(docRef => {
        this.refreshMetersForProperty(meterData.propertyUID);
        return docRef.id;
      }),
      catchError(error => {
        console.error('Error creating meter:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update an existing meter
   */
  updateMeter(propertyUID: string, meterUID: string, updates: UpdateMeterRequest): Observable<void> {
    const meterDoc = this.getMeterDoc(propertyUID, meterUID);
    const now = Timestamp.now();

    // Strip undefined fields — the form dialog may pass optional fields as
    // `undefined` (e.g. a cleared `eanCode`), which Firestore rejects.
    // The provider object is rebuilt with Timestamp dates and no nested
    // `undefined` keys (`filterUndefinedValues` only sanitises top-level keys).
    const updateData = this.filterUndefinedValues({
      ...updates,
      provider: toStoredMeterProvider(updates.provider),
      updatedAt: now
    });

    return from(updateDoc(meterDoc, updateData)).pipe(
      tap(() => {
        this.refreshMetersForProperty(propertyUID);
      }),
      catchError(error => {
        console.error('Error updating meter:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a meter (soft delete)
   */
  deleteMeter(propertyUID: string, meterUID: string): Observable<void> {
    const meterDoc = this.getMeterDoc(propertyUID, meterUID);
    const now = Timestamp.now();
    
    const deleteData = {
      deleted: true,
      updatedAt: now
    };

    return from(updateDoc(meterDoc, deleteData)).pipe(
      tap(() => {
        this.refreshMetersForProperty(propertyUID);
      }),
      catchError(error => {
        console.error('Error deleting meter:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Add a new meter reading
   */
  addMeterReading(propertyUID: string, meterUID: string, reading: AddMeterReadingRequest): Observable<void> {
    return this.getMeterById(propertyUID, meterUID).pipe(
      switchMap(meter => {
        if (!meter) {
          return throwError(() => new Error('Meter not found'));
        }

        const meterDoc = this.getMeterDoc(propertyUID, meterUID);
        const now = Timestamp.now();
        
        // Filter out undefined values from the reading data
        const cleanedReading = this.filterUndefinedValues(reading);
        
        const newReading: MeterReading = {
          ...cleanedReading,
          value: reading.value,
          type: reading.type,
          date: Timestamp.fromDate(reading.date)
        };

        // Add the new reading to the readings array and update current reading
        const updatedReadings = [...meter.readings, newReading];
        
        // Filter out undefined values from the update data
        const updateData = this.filterUndefinedValues({
          readings: updatedReadings,
          currentReading: reading.value,
          currentReadingDate: Timestamp.fromDate(reading.date),
          updatedAt: now
        });

        return from(updateDoc(meterDoc, updateData));
      }),
      tap(() => {
        this.refreshMetersForProperty(propertyUID);
      }),
      catchError(error => {
        console.error('Error adding meter reading:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Transfer meter contract to new holder
   */
  transferMeterContract(propertyUID: string, meterUID: string, newContractHolder: 'owner' | 'tenant'): Observable<void> {
    const updateData: UpdateMeterRequest = {
      contractHolder: newContractHolder
    };

    return this.updateMeter(propertyUID, meterUID, updateData);
  }

  /**
   * Get meter statistics for a property
   */
  getMeterStatistics(propertyUID: string): Observable<{
    totalMeters: number;
    metersByType: Record<string, number>;
    activeMeters: number;
    tenantMeters: number;
    ownerMeters: number;
  }> {
    return this.getMetersByProperty(propertyUID).pipe(
      map(meters => {
        return {
          totalMeters: meters.length,
          metersByType: meters.reduce((acc, meter) => {
            acc[meter.type] = (acc[meter.type] || 0) + 1;
            return acc;
          }, {} as Record<string, number>),
          activeMeters: meters.filter(meter => meter.status === 'active').length,
          tenantMeters: meters.filter(meter => meter.contractHolder === 'tenant').length,
          ownerMeters: meters.filter(meter => meter.contractHolder === 'owner').length
        };
      })
    );
  }

  /**
   * Upload a photo to Storage and attach it to the meter as a `photo` document.
   *
   * The Storage security rules only allow writes under
   * `workspaces/{workspaceId}/documents/**`, so we derive the workspace id from
   * the (already workspace-scoped) meters path and upload under
   * `documents/meter/{meterUID}/`. The full storage path is persisted on the
   * `MeterDocument` so the blob can be deleted again when the photo is removed.
   */
  uploadMeterPhoto(propertyUID: string, meterUID: string, file: File): Observable<void> {
    return this.uploadMeterFile(propertyUID, meterUID, file, 'photo');
  }

  /**
   * Generic version of {@link uploadMeterPhoto}: upload any file (photo, PDF, …)
   * to Storage and attach it to the meter as a document of the given `type`.
   */
  uploadMeterFile(
    propertyUID: string,
    meterUID: string,
    file: File,
    type: MeterDocument['type']
  ): Observable<void> {
    const metersPath = this.metersPath(propertyUID);
    const workspaceId = metersPath.split('/')[1];
    const safeName = file.name.replace(/[^A-Za-z0-9._-]+/g, '_');
    const storagePath = `workspaces/${workspaceId}/documents/meter/${meterUID}/${Date.now()}_${safeName}`;
    const storageRef = ref(this.storage, storagePath);

    return from(uploadBytes(storageRef, file)).pipe(
      switchMap(snapshot => from(getDownloadURL(snapshot.ref))),
      switchMap(url =>
        this.addMeterDocument(propertyUID, meterUID, {
          name: file.name,
          type,
          url,
          size: file.size,
          mimeType: file.type,
          storagePath
        })
      ),
      catchError(error => {
        console.error('Error uploading meter file:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Add a document to a meter
   */
  addMeterDocument(propertyUID: string, meterUID: string, document: Omit<MeterDocument, 'id' | 'uploadDate'>): Observable<void> {
    return this.getMeterById(propertyUID, meterUID).pipe(
      switchMap(meter => {
        if (!meter) {
          return throwError(() => new Error('Meter not found'));
        }

        const meterDoc = this.getMeterDoc(propertyUID, meterUID);
        const now = Timestamp.now();
        
        const newDocument: MeterDocument = {
          ...document,
          id: this.generateDocumentId(),
          uploadDate: now
        };

        // Add the new document to the documents array
        const updatedDocuments = [...meter.documents, newDocument];
        
        const updateData = {
          documents: updatedDocuments,
          updatedAt: now
        };

        return from(updateDoc(meterDoc, updateData));
      }),
      tap(() => {
        this.refreshMetersForProperty(propertyUID);
      }),
      catchError(error => {
        console.error('Error adding meter document:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Remove a document from a meter
   */
  removeMeterDocument(propertyUID: string, meterUID: string, documentId: string): Observable<void> {
    return this.getMeterById(propertyUID, meterUID).pipe(
      switchMap(meter => {
        if (!meter) {
          return throwError(() => new Error('Meter not found'));
        }

        const meterDoc = this.getMeterDoc(propertyUID, meterUID);
        const now = Timestamp.now();

        // Remove the document from the documents array
        const removed = meter.documents.find(doc => doc.id === documentId);
        const updatedDocuments = meter.documents.filter(doc => doc.id !== documentId);

        const updateData = {
          documents: updatedDocuments,
          updatedAt: now
        };

        return from(updateDoc(meterDoc, updateData)).pipe(
          // Best-effort Storage cleanup — never fail the removal if the blob is
          // already gone or its path was not recorded (legacy documents).
          switchMap(() => {
            if (!removed?.storagePath) return of(void 0);
            return from(deleteObject(ref(this.storage, removed.storagePath))).pipe(
              catchError(storageError => {
                console.warn('Could not delete meter document from storage:', storageError);
                return of(void 0);
              })
            );
          })
        );
      }),
      tap(() => {
        this.refreshMetersForProperty(propertyUID);
      }),
      catchError(error => {
        console.error('Error removing meter document:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get meters that need attention (readings, status, etc.)
   */
  getMetersNeedingAttention(propertyUID: string): Observable<{
    noRecentReadings: Meter[];
    inactive: Meter[];
  }> {
    return this.getMetersByProperty(propertyUID).pipe(
      map(meters => {
        const now = new Date();
        const threeMonthsAgo = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
        
        return {
          noRecentReadings: meters.filter(meter => {
            if (!meter.currentReadingDate) return true;
            const lastReadingDate = meter.currentReadingDate.toDate();
            return lastReadingDate < threeMonthsAgo;
          }),
          inactive: meters.filter(meter => meter.status !== 'active')
        };
      })
    );
  }

  /**
   * Validate EAN code uniqueness within a property
   */
  validateEanCodeUniqueness(propertyUID: string, eanCode: string, excludeMeterUID?: string): Observable<boolean> {
    return this.getMetersByProperty(propertyUID).pipe(
      map(meters => {
        const existingMeters = meters.filter(meter => 
          meter.eanCode === eanCode && 
          (!excludeMeterUID || meter.meterUID !== excludeMeterUID)
        );
        
        return existingMeters.length === 0; // True if unique
      }),
      catchError(error => {
        console.error('Error validating EAN code:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Private helper methods
   */
  private refreshMetersForProperty(propertyUID: string): void {
    // Force a fresh fetch using the existing load method
    this.loadMetersForProperty(propertyUID);
  }

  private generateDocumentId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Filter out undefined and null values from an object
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private filterUndefinedValues<T extends Record<string, any>>(obj: T): Partial<T> {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null) {
        acc[key as keyof T] = value;
      }
      return acc;
    }, {} as Partial<T>);
  }

  /**
   * Utility methods for UI components
   */
  getMeterTypeDisplayName = MeterUtils.getMeterTypeDisplayName;
  getMeterSubTypeDisplayName = MeterUtils.getMeterSubTypeDisplayName;
  getSubTypesForType = MeterUtils.getSubTypesForType;
  validateEanCode = MeterUtils.validateEanCode;
  getContractHolderDisplayName = MeterUtils.getContractHolderDisplayName;
  calculateConsumption = MeterUtils.calculateConsumption;
  getStatusColor = MeterUtils.getStatusColor;
} 